import { useState } from "react"
import { trpc } from "~/lib/trpc"
import { Button } from "@aix/ui"

export default function BrainDumpsTab() {
  const [limit] = useState(50)
  const [offset, setOffset] = useState(0)

  const { data, isLoading, refetch } = trpc.trackables.listBrainDumps.useQuery({
    limit,
    offset,
  })

  const formatDate = (date: Date | string) => {
    const d = new Date(date)
    return d.toLocaleString("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric",
      hour: "numeric",
      minute: "2-digit",
      hour12: true,
    })
  }

  const formatDuration = (seconds: number | null) => {
    if (!seconds) return null
    const mins = Math.floor(seconds / 60)
    const secs = seconds % 60
    return `${mins}:${secs.toString().padStart(2, "0")}`
  }

  const getAudioUrl = (r2Key: string, r2Bucket: string) => {
    // Construct Cloudflare R2 public URL
    // Assuming you have a public domain set up for your R2 bucket
    return `https://pub-e743ae6f90074bb5bf6c8c0b9bf785b8.r2.dev/${r2Key}`
  }

  if (isLoading) {
    return (
      <div className="p-6">
        <div className="text-center py-12 text-muted-foreground">Loading brain dumps...</div>
      </div>
    )
  }

  const brainDumps = data?.brainDumps || []
  const total = data?.total || 0

  return (
    <div className="p-6 space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-2xl font-bold text-primary">Brain Dumps</h2>
          <p className="text-sm text-muted-foreground mt-1">{total} total check-ins</p>
        </div>
        <Button onClick={() => refetch()} variant="outline" className="border-2 border-border">
          🔄 Refresh
        </Button>
      </div>

      {/* List */}
      <div className="space-y-4">
        {brainDumps.length === 0 ? (
          <div className="bg-secondary border-4 border-border p-12 text-center">
            <p className="text-muted-foreground text-lg">No brain dumps yet</p>
            <p className="text-sm text-muted-foreground mt-2">Create check-ins from the mobile app to see them here</p>
          </div>
        ) : (
          brainDumps.map((dump: any) => (
            <div key={dump.id} className="bg-secondary border-4 border-border p-6 shadow-lg">
              {/* Header Row */}
              <div className="flex items-start justify-between mb-4">
                <div className="flex items-center gap-3">
                  {/* Media Type Icon */}
                  <div className="text-3xl">
                    {dump.mediaType === "audio" && "🎤"}
                    {dump.mediaType === "video" && "🎥"}
                    {dump.mediaType === "text" && "📝"}
                    {!dump.mediaType && "💭"}
                  </div>

                  {/* Date and Metadata */}
                  <div>
                    <div className="font-semibold text-primary">{formatDate(dump.createdAt)}</div>
                    <div className="text-xs text-muted-foreground flex items-center gap-2 mt-1">
                      {dump.checkInDate && <span>Check-in Date: {dump.checkInDate}</span>}
                      {dump.duration && <span>• Duration: {formatDuration(dump.duration)}</span>}
                      {dump.transcriptionStatus && (
                        <span
                          className={`px-2 py-0.5 rounded text-xs ${
                            dump.transcriptionStatus === "completed"
                              ? "bg-green-100 text-green-700"
                              : dump.transcriptionStatus === "failed"
                                ? "bg-red-100 text-red-700"
                                : "bg-yellow-100 text-yellow-700"
                          }`}
                        >
                          {dump.transcriptionStatus}
                        </span>
                      )}
                    </div>
                  </div>
                </div>

                {/* Favorite Star */}
                {dump.isFavorite && <div className="text-2xl">⭐</div>}
              </div>

              {/* Audio Player (inline) */}
              {dump.mediaType === "audio" && dump.r2Key && (
                <div className="mb-4">
                  <audio
                    controls
                    className="w-full"
                    preload="metadata"
                    src={getAudioUrl(dump.r2Key, dump.r2Bucket || "ghostcontent-uploads")}
                  >
                    Your browser does not support the audio element.
                  </audio>
                </div>
              )}

              {/* Video Player (inline) */}
              {dump.mediaType === "video" && dump.r2Key && (
                <div className="mb-4">
                  <video
                    controls
                    className="w-full max-h-96 bg-black"
                    preload="metadata"
                    src={getAudioUrl(dump.r2Key, dump.r2Bucket || "ghostcontent-uploads")}
                  >
                    Your browser does not support the video element.
                  </video>
                </div>
              )}

              {/* Transcription */}
              {dump.transcription && (
                <div className="bg-background border-2 border-border p-4 rounded">
                  <div className="text-sm text-muted-foreground mb-2 font-semibold">
                    Transcription
                    {dump.transcriptionProvider && (
                      <span className="ml-2 text-xs">(via {dump.transcriptionProvider})</span>
                    )}
                  </div>
                  <div className="text-sm whitespace-pre-wrap">{dump.transcription}</div>
                </div>
              )}

              {/* Transcription Error */}
              {dump.transcriptionError && (
                <div className="bg-red-50 border-2 border-red-500 p-3 rounded text-sm text-red-700">
                  <span className="font-semibold">Error:</span> {dump.transcriptionError}
                </div>
              )}

              {/* Metadata */}
              {dump.metadata && Object.keys(dump.metadata).length > 0 && (
                <details className="mt-4">
                  <summary className="text-sm text-muted-foreground cursor-pointer hover:text-primary">
                    Show metadata
                  </summary>
                  <pre className="text-xs bg-background border-2 border-border p-3 rounded mt-2 overflow-auto">
                    {JSON.stringify(dump.metadata, null, 2)}
                  </pre>
                </details>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {total > limit && (
        <div className="flex items-center justify-center gap-4">
          <Button
            onClick={() => setOffset(Math.max(0, offset - limit))}
            disabled={offset === 0}
            variant="outline"
            className="border-2 border-border"
          >
            ← Previous
          </Button>
          <span className="text-sm text-muted-foreground">
            Showing {offset + 1}-{Math.min(offset + limit, total)} of {total}
          </span>
          <Button
            onClick={() => setOffset(offset + limit)}
            disabled={offset + limit >= total}
            variant="outline"
            className="border-2 border-border"
          >
            Next →
          </Button>
        </div>
      )}
    </div>
  )
}
